// Pure, framework-free helpers for the GraphQL selection-set tree.
//
// The selection is held as a flat Set of encoded field paths (design section 7.5): the persisted
// GraphQL string is the source of truth, and checkbox tri-state is *derived* from this set plus the
// lazily-loaded schema, never stored on the DOM nodes. Serialization back to a GraphQL string is
// done here on the client (design section 6.1); only parsing crosses to the backend.

const SEP = "\u0001"; // separates path segments
const FRAG = "\u0002"; // marks an inline-fragment (... on Type) segment
const TYPENAME = "__typename";

export type SelectionSet = Set<string>;

export function fieldSegment(name: string): string {
  return name;
}

export function fragmentSegment(onType: string): string {
  return FRAG + onType;
}

export function isFragmentSegment(segment: string): boolean {
  return segment.startsWith(FRAG);
}

export function onTypeOf(segment: string): string {
  return segment.slice(FRAG.length);
}

export function pathKey(segments: string[]): string {
  return segments.join(SEP);
}

export function pathSegments(key: string): string[] {
  return key === "" ? [] : key.split(SEP);
}

/** True if the set contains any path strictly below the given prefix. */
export function hasDescendant(set: SelectionSet, prefix: string[]): boolean {
  const prefixKey = prefix.length ? pathKey(prefix) + SEP : "";
  return Array.from(set).some((key) =>
    prefixKey === "" ? key !== "" : key.startsWith(prefixKey)
  );
}

/** Removes the given path and every descendant of it. */
export function removeSubtree(set: SelectionSet, path: string[]): void {
  // The root path ([]) covers the whole selection, so removing it clears everything (the
  // childPrefix loop below never matches real paths for an empty prefix).
  if (path.length === 0) {
    set.clear();
    return;
  }
  const key = pathKey(path);
  const childPrefix = key + SEP;
  set.delete(key);
  Array.from(set).forEach((existing) => {
    if (existing.startsWith(childPrefix)) {
      set.delete(existing);
    }
  });
}

/** Flattens a structured selection (design section 5.2) into the flat path set. */
export function hydrate(json: any): SelectionSet {
  const set: SelectionSet = new Set();
  walk(json, []);
  return set;

  function walk(node: any, prefix: string[]): void {
    (node?.fields ?? []).forEach((field: any) => {
      const path = [...prefix, fieldSegment(field.name)];
      set.add(pathKey(path));
      if (field.fields || field.inlineFragments) {
        walk(field, path);
      }
    });
    (node?.inlineFragments ?? []).forEach((fragment: any) => {
      walk(fragment, [...prefix, fragmentSegment(fragment.onType)]);
    });
  }
}

interface TreeNode {
  segment: string;
  children: Map<string, TreeNode>;
}

/**
 * Serializes the flat path set into a GraphQL selection string including braces, mirroring the
 * format produced by the backend {@code SelectionSetGenerator.generate()}. Branch nodes always
 * carry at least one child by construction (the tree adds {@code __typename} when a branch would
 * otherwise be empty), so a childless node is always a scalar leaf.
 */
export function serialize(set: SelectionSet): string {
  const root: TreeNode = { segment: "", children: new Map() };
  Array.from(set).forEach((key) => {
    let node = root;
    pathSegments(key).forEach((segment) => {
      let child = node.children.get(segment);
      if (!child) {
        child = { segment, children: new Map() };
        node.children.set(segment, child);
      }
      node = child;
    });
  });
  return `{ ${serializeChildren(root)} }`;
}

function serializeChildren(node: TreeNode): string {
  const parts: string[] = [];
  node.children.forEach((child, segment) => {
    if (isFragmentSegment(segment)) {
      parts.push(`... on ${onTypeOf(segment)} { ${serializeChildren(child)} }`);
    } else if (child.children.size > 0) {
      parts.push(`${segment} { ${serializeChildren(child)} }`);
    } else {
      parts.push(segment);
    }
  });
  return parts.length ? parts.join(" ") : TYPENAME;
}

export { TYPENAME };
